import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import { MISSION_CATEGORIES, COUNTRIES, PLATFORM_FEE_PERCENT } from '../types';
import { t } from '../lib/i18n';
import toast from 'react-hot-toast';

export const CreateMissionPage: React.FC = () => {
  const { currentUser, createMission, lang } = useStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    title: '',
    description: '',
    reward: '',
    estimatedHours: '',
    category: 'Errands',
    location: '',
    skillsRaw: '',
  });

  if (!currentUser) {
    return (
      <div className="page-container">
        <div className="empty-state">
          <span className="icon">🔒</span>
          <p>You must be signed in to post a bounty.</p>
          <button className="btn btn-gold" onClick={() => navigate('/')}>Go Home</button>
        </div>
      </div>
    );
  }

  const reward = parseFloat(form.reward) || 0;
  const fee = Math.round(reward * PLATFORM_FEE_PERCENT * 100) / 100;
  const total = Math.round((reward + fee) * 100) / 100;
  const showFeePreview = reward >= 5;

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const skills = form.skillsRaw
      .split(',').map(s => s.trim()).filter(Boolean).slice(0, 10);
    const err = createMission({
      title: form.title,
      description: form.description,
      reward,
      estimatedHours: parseFloat(form.estimatedHours) || 0,
      category: form.category,
      location: form.location || 'Remote',
      skills,
    });
    setLoading(false);
    if (err) { toast.error(t(lang, err)); return; }
    toast.success(t(lang, 'toast_mission_created'));
    navigate('/missions');
  };

  return (
    <div className="page-container">
      <div className="section-header">
        <h2>{t(lang, 'create_title')}</h2>
      </div>

      <div className="form-card" style={{ maxWidth: 640, margin: '0 auto' }}>
        <p className="form-notice">
          ⚠ You will be charged the full bounty amount upfront.
          Platform fee: {(PLATFORM_FEE_PERCENT * 100).toFixed(0)}%.
          Only legal tasks permitted.
        </p>

        <form onSubmit={handleSubmit} noValidate>
          {/* Title */}
          <div className="form-group">
            <label className="form-label">Mission Title *</label>
            <input className="form-input" value={form.title} onChange={set('title')}
              placeholder="e.g. Buy groceries at Monoprix…" maxLength={120} required />
          </div>

          {/* Reward + Hours */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Reward (€) *</label>
              <input className="form-input" type="number" value={form.reward} onChange={set('reward')}
                placeholder="50" min="5" max="999999" step="0.01" required />
            </div>
            <div className="form-group">
              <label className="form-label">Est. Hours *</label>
              <input className="form-input" type="number" value={form.estimatedHours} onChange={set('estimatedHours')}
                placeholder="2" min="0.5" max="2000" step="0.5" required />
            </div>
          </div>

          {/* Category + Location */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-input" value={form.category} onChange={set('category')}>
                {MISSION_CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Location</label>
              <input className="form-input" value={form.location} onChange={set('location')}
                placeholder="Paris, France or Remote" maxLength={120} />
            </div>
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">Description *</label>
            <textarea className="form-input" value={form.description} onChange={set('description')}
              rows={5} placeholder="Describe your mission in detail…" maxLength={2000} required />
            <span className="form-hint">{form.description.length}/2000</span>
          </div>

          {/* Skills */}
          <div className="form-group">
            <label className="form-label">Required Skills <span className="form-hint">(comma-separated, max 10)</span></label>
            <input className="form-input" value={form.skillsRaw} onChange={set('skillsRaw')}
              placeholder="e.g. Driving, French, Negotiation" />
          </div>

          {/* Fee preview */}
          {showFeePreview && (
            <div className="fee-preview">
              <div className="fee-row">
                <span>{t(lang, 'create_amount')}</span>
                <strong>€{reward.toFixed(2)}</strong>
              </div>
              <div className="fee-row">
                <span>{t(lang, 'create_platform_fee')}</span>
                <strong>€{fee.toFixed(2)}</strong>
              </div>
              <div className="fee-row fee-total">
                <span>{t(lang, 'create_total')}</span>
                <strong>€{total.toFixed(2)}</strong>
              </div>
            </div>
          )}

          {/* Stripe note */}
          <div className="stripe-note">
            🔒 Payment secured by Stripe.
            Set <code>REACT_APP_STRIPE_PUBLISHABLE_KEY</code> in your <code>.env</code> to enable live payments.
          </div>

          <button className="btn btn-gold btn-full" type="submit" disabled={loading}>
            {loading ? 'Processing…' : `🏹 ${t(lang, 'create_submit')}`}
          </button>
        </form>
      </div>
    </div>
  );
};
